import { FC, useEffect, useState, useCallback, memo } from 'react';
import {
  Button,
  TextBody,
  Spacer,
  Stack,
  Icon,
  AccordionGroup,
  AccordionItem,
  BlockList,
  Input,
  Textarea,
  Divider,
  IconButton,
  Tooltip,
} from '@pypestream/design-system';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { tableStore } from '../../../stores/table-store';
import { useDeleteTaskPopover } from '../../popovers';

interface TaskField {
  id: string; // React Hook Form's internal ID
  name: string;
  description?: string;
  taskId?: string; // The actual task ID from the server
}

// Memoized task item component to prevent unnecessary re-renders
/* eslint-disable react/prop-types */
const TaskItem: FC<{
  field: { id: string };
  index: number;
  isExpanded: boolean;
  onShow: () => void;
  onHide: () => void;
  onRemove: () => void;
}> = memo(({ index, isExpanded, onShow, onHide, onRemove }) => {
  const { control, register, setValue, getValues } = useFormContext();

  // Only watch the specific task's name for the header display
  const taskName = useWatch({
    control,
    name: `tasks.${index}.name`,
    defaultValue: '',
  });

  // Get taskId from form values
  const taskId = useWatch({
    control,
    name: `tasks.${index}.taskId`,
    defaultValue: undefined,
  });

  const { deleteTaskPopover, trigger } = useDeleteTaskPopover({
    taskName: taskName || undefined,
    onDelete: onRemove,
  });

  return (
    <AccordionItem open={isExpanded} onShow={onShow} onHide={onHide}>
      <Stack
        slot="header"
        justifyContent="space-between"
        alignItems="center"
        gutter="xsmall"
        nowrap
        style={{ flexGrow: 1 }}
      >
        <Stack alignItems="center" gutter="xsmall" nowrap>
          <Icon size="small" name="target" color="purple" />
          <TextBody size="small">{taskName || ''}</TextBody>
        </Stack>
        <IconButton
          id={trigger}
          name="delete"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
          }}
        />
        {deleteTaskPopover}
      </Stack>
      <BlockList gutter="xsmall">
        <div>
          <TextBody size="small">Task Name</TextBody>
          <Spacer size="2xsmall" />
          <Input
            variant="outlined"
            placeholder="Order shoes"
            {...register(`tasks.${index}.name`)}
            onInput={(e) => {
              // @ts-expect-error - TODO: fix this
              const { value } = e.target;
              setValue(`tasks.${index}.name`, value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
            onChange={({ target: { value } }) => {
              if (taskId) {
                const currentTask = getValues(`tasks.${index}`) as TaskField;
                tableStore.updateTaskNode(taskId, {
                  name: value,
                  description: currentTask?.description || '',
                });
              }
            }}
          />
        </div>
        <div>
          <Stack alignItems="center" gutter="2xsmall">
            <TextBody size="small">Example Phrases*</TextBody>
            <Tooltip
              placement="right"
              content="Write at least one natural way someone might ask for this. The Supervisor Agent uses these Example Phrases to understand what the user wants and route them to the right workflow."
            >
              <Icon name="info" color="gray" size="xsmall" />
            </Tooltip>
          </Stack>
          <Spacer size="2xsmall" />
          <Textarea
            variant="outlined"
            placeholder="I want to buy shoes | Help me order sneakers"
            {...register(`tasks.${index}.description`)}
            onChange={({ target: { value } }) => {
              if (taskId) {
                const currentTask = getValues(`tasks.${index}`) as TaskField;
                tableStore.updateTaskNode(taskId, {
                  name: currentTask?.name || '',
                  description: value,
                });
              }
            }}
          />
        </div>
      </BlockList>
    </AccordionItem>
  );
});

TaskItem.displayName = 'TaskItem';

export const TabTasks: FC = () => {
  const { control, getValues } = useFormContext();

  const {
    fields,
    append,
    remove: removeTask,
  } = useFieldArray({
    control,
    name: 'tasks',
  });

  const [expandedTaskIds, setExpandedTaskIds] = useState<Set<number>>(
    new Set()
  );

  // Sync tasks with nodes when component mounts or tasks change
  // Use getValues instead of useWatch to avoid re-renders
  useEffect(() => {
    const tasks = getValues('tasks') as TaskField[];
    if (!tasks) return;

    const tasksWithIds = tasks
      .filter((task: TaskField) => task.taskId)
      .map((task: TaskField) => ({
        id: task.taskId!,
        name: task.name,
        description: task.description || '',
      }));

    if (tasksWithIds.length > 0) {
      tableStore.syncTasksWithNodes(tasksWithIds);
    }
  }, [fields, getValues]);

  const addNewTask = useCallback(() => {
    const newTask = {
      name: '',
      description: '',
      // No taskId for new tasks - they will be created on the server
    };
    append(newTask);
    setExpandedTaskIds((prev) => new Set(prev).add(fields.length));
  }, [append, fields.length]);

  // Memoize callbacks to prevent unnecessary re-renders of TaskItem
  const handleShow = useCallback((index: number) => {
    setExpandedTaskIds((prev) => new Set(prev).add(index));
  }, []);

  const handleHide = useCallback((index: number) => {
    setExpandedTaskIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  }, []);

  const handleRemove = useCallback(
    (index: number) => {
      removeTask(index);
    },
    [removeTask]
  );

  return (
    <>
      <Stack nowrap gutter="small"></Stack>
      <Spacer />
      <AccordionGroup multiple>
        {fields.map((field, index) => (
          <TaskItem
            key={field.id}
            field={field}
            index={index}
            isExpanded={expandedTaskIds.has(index)}
            onShow={() => handleShow(index)}
            onHide={() => handleHide(index)}
            onRemove={() => handleRemove(index)}
          />
        ))}
      </AccordionGroup>
      <Divider />
      <Spacer size="xsmall" />
      <Button size="large" variant="ghost" onClick={addNewTask}>
        <Icon
          name="plus"
          size="small"
          color="purple"
          variant="filled"
          slot="prefix"
        />
        Add task
      </Button>
    </>
  );
};
